import { Study, StudyReportSummary } from "../../types/Rows";
import { formatDateTime } from "./formatDate";
import { ListBoxOption } from "./ListBox";
import { ALL_TIME } from "../constants";
import { cn } from "../../lib/utils";

const SPECIALTY_MAP = [
  { key: "hematologia", label: "hematologia", accent: "#5B8FD8", badge: "bg-[#EDF3FC] text-[#3570C4]" },
  { key: "kardiologia", label: "kardiologia", accent: "#2BAE72", badge: "bg-[#E6F7F1] text-[#1A8055]" },
  { key: "pulmonologia", label: "pulmonologia", accent: "#F4A832", badge: "bg-[#FEF3E2] text-[#C07810]" },
  { key: "neurologia", label: "neurologia", accent: "#9060D0", badge: "bg-[#F3EEF9] text-[#7040B0]" },
  { key: "onkologia", label: "onkologia", accent: "#E05050", badge: "bg-[#FEE8E8] text-[#C03030]" },
];

export const SPECIALTY_KEYS = SPECIALTY_MAP.map((s) => s.key);

function detectSpecialties(description?: string) {
  if (!description) return [];
  const lower = description.toLowerCase();
  return SPECIALTY_MAP.filter(({ key }) => lower.includes(key));
}

const STATUS_PILLS = [
  { key: "toVerify",   label: "Do weryfikacji", bg: "bg-[#FEF3E2]", text: "text-[#C07810]" },
  { key: "toCorrect",  label: "Do korekty",     bg: "bg-[#FEE8E8]", text: "text-[#C03030]" },
  { key: "accepted",   label: "Zaakceptowane",  bg: "bg-[#E6F7F1]", text: "text-[#1A8055]" },
  { key: "inProgress", label: "W trakcie",      bg: "bg-[#F0F0F0]", text: "text-[#666]"    },
] as const;

export default function StoryView({
  currentFilter = ALL_TIME,
  data,
  urlPrefix,
  summary,
  additionalInfoFactory = (_data: any) => null,
}: {
  currentFilter?: ListBoxOption;
  data: Study;
  urlPrefix: string;
  summary?: StudyReportSummary;
  additionalInfoFactory?: (data: any) => any;
}) {
  const specialties = detectSpecialties(data.description);
  const accentColor = specialties[0]?.accent ?? "#EBEBEA";
  const resortsCount = data.resorts?.length ?? 0;

  return (
    <a
      href={urlPrefix + data.id}
      id={`story-${data.id}`}
      className="flex items-stretch bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[var(--sc-radius-lg)] overflow-hidden transition-all duration-150 hover:border-[var(--sc-muted)] hover:shadow-sm"
    >
      <div className="w-1 flex-shrink-0" style={{ background: accentColor }} />
      <div className="flex flex-1 items-center gap-6 px-5 py-4">
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-[var(--sc-text)] leading-snug mb-1.5 truncate">
            {data.name}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {specialties.map(({ key, label, badge }) => (
              <span key={key} className={cn("text-[11px] font-medium px-2 py-0.5 rounded", badge)}>
                {label}
              </span>
            ))}
            {data.createdBy && (
              <span className="text-[11px] text-[var(--sc-muted)]">{data.createdBy}</span>
            )}
            {data.created && (
              <span className="text-[11px] text-[var(--sc-muted)]">{formatDateTime(data.created)}</span>
            )}
          </div>
        </div>

        {summary && summary.total > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {STATUS_PILLS.map(({ key, label, bg, text }) => {
              const count = summary[key];
              if (count === 0) return null;
              return (
                <span
                  key={key}
                  title={label}
                  className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", bg, text)}
                >
                  {count}
                </span>
              );
            })}
            <div className="w-px h-5 bg-[var(--sc-border)] mx-1" />
            <div className="text-right">
              <div className="text-[18px] font-light text-[var(--sc-text)] leading-none">{summary.total}</div>
              <div className="text-[10px] text-[var(--sc-muted)] uppercase tracking-wider mt-0.5">Raporty</div>
            </div>
          </div>
        )}

        {resortsCount > 0 && (
          <div className="flex items-center gap-4 flex-shrink-0">
            {summary && summary.total > 0 && <div className="w-px h-8 bg-[var(--sc-border)]" />}
            <div className="text-right">
              <div className="text-[18px] font-light text-[var(--sc-text)] leading-none">{resortsCount}</div>
              <div className="text-[10px] text-[var(--sc-muted)] uppercase tracking-wider mt-0.5">Ośrodki</div>
            </div>
          </div>
        )}

        {additionalInfoFactory(data)}
        <span className="text-[var(--sc-border)] text-lg flex-shrink-0">›</span>
      </div>
    </a>
  );
}

export function BaseItemView({
  data,
  urlPrefix,
}: {
  data: { id: string; name: string; created: Date; createdBy?: string };
  urlPrefix?: string;
}) {
  return (
    <a
      href={(urlPrefix ?? "") + data.id}
      id={`story-${data.id}`}
      className="flex bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[var(--sc-radius-lg)] px-5 py-4 hover:border-[var(--sc-muted)] hover:shadow-sm transition-all duration-150"
    >
      <p className="text-[15px] font-medium text-[var(--sc-text)]">{data.name}</p>
    </a>
  );
}
